import { inject, Injectable, Provider } from '@angular/core'
import { NoPreloading, PreloadingStrategy, Route } from '@angular/router'

import { concatMap, EMPTY, Observable, Subject, timer } from 'rxjs'

import { CONFIG } from '@ti/core/contracts/config'

@Injectable()
export class CustomPreloadingStrategy implements PreloadingStrategy {
  protected readonly config = inject(CONFIG)

  protected readonly INITIAL_TIMEOUT_MS = 30_000
  protected readonly TIMEOUT_INCREMENT_MS = 2000

  protected readonly queue$ = new Subject<{ delay: number; load: () => Observable<unknown> }>()
  protected readonly queue = this.queue$
    .pipe(concatMap(({ delay, load }) => timer(delay).pipe(concatMap(() => load()))))
    .subscribe()

  protected preloadedRoutesCount = 0

  public preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (this.config.envType === 'production' || route.data?.['preload'] === false) {
      return EMPTY
    }

    this.preloadedRoutesCount++
    const delay = this.INITIAL_TIMEOUT_MS + this.preloadedRoutesCount * this.TIMEOUT_INCREMENT_MS

    return new Observable((observer) => {
      this.queue$.next({
        delay,
        load: () => {
          const result = load()
          result.subscribe(observer)
          return result
        },
      })
    })
  }
}

export const provideDefaultPreloadingStrategy = (): Provider[] => [
  { provide: PreloadingStrategy, useExisting: NoPreloading },
]

export const provideCustomPreloadingStrategy = (): Provider[] => [
  { provide: PreloadingStrategy, useClass: CustomPreloadingStrategy },
]
